import { flow } from "fp-ts/lib/function";
import { type Lens } from "monocle-ts";

import { type OrganicStatsResponseDimensionEntry, UserDimension } from "../../../gql-generated/types";
import { type GetOrganicStatsQuery } from "../../api/queries";
import { isStatsListEmpty } from "../../derived-data";
import { ejectReportDimensionStatsList, sortStatsListInCascadingOrder } from "../../filters";
import {
    type DimensionFunnel,
    type DimensionFunnelMap,
    createDefaultDimensionsFunnelMap,
    makeDimensionLens,
} from "../../models/dimension-funnel";

import { mapDimensionFunnel } from "./mapDimensionFunnel";

export const mapDimensionBreakdownFunnelData = (
    data: GetOrganicStatsQuery,
    dataTotal: GetOrganicStatsQuery,
): {
    dimensionFunnelMap: DimensionFunnelMap;
    isEmpty: boolean;
} => {
    const [responseEntry] = data.getMyLensesOrganicStats.responseEntryList;
    const [responseEntryTotal] = dataTotal.getMyLensesOrganicStats.responseEntryList;

    const statsList: readonly OrganicStatsResponseDimensionEntry[] = responseEntry
        ? responseEntry.statsResponseTimeEntryList[0]?.statsList ?? []
        : [];

    const statsListTotal: readonly OrganicStatsResponseDimensionEntry[] = responseEntryTotal
        ? responseEntryTotal.statsResponseTimeEntryList[0]?.statsList ?? []
        : [];

    const [age, os, country, ageGender, total]: readonly [
        OrganicStatsResponseDimensionEntry[],
        Lens<DimensionFunnelMap, DimensionFunnel>,
    ][] = [
        UserDimension.Age,
        UserDimension.Os,
        UserDimension.Country,
        UserDimension.AgeGender,
        UserDimension.UserDimensionUnset,
    ].map((reportDimension) => {
        const statsListFilteredSorted = flow(
            ejectReportDimensionStatsList(reportDimension),
            sortStatsListInCascadingOrder,
        )(statsList);

        switch (reportDimension) {
            case UserDimension.AgeGender:
                return [statsListFilteredSorted, makeDimensionLens("GENDER")];

            case UserDimension.UserDimensionUnset:
                return [sortStatsListInCascadingOrder(statsListTotal), makeDimensionLens("TOTAL")];

            default:
                return [statsListFilteredSorted, makeDimensionLens(reportDimension)];
        }
    });

    if (!age || !os || !country || !ageGender || !total) {
        throw new Error("Couldn't map dimension breakdown funnel data correctly.");
    }

    const [ageStats, ageLens] = age;
    const [osStats, osLens] = os;
    const [countryStats, countryLens] = country;
    const [ageGenderStats, genderLens] = ageGender;
    const [totalStats, totalLens] = total;
    const isEmpty = isStatsListEmpty(totalStats);
    return {
        dimensionFunnelMap: flow(
            ageLens.modify(mapDimensionFunnel(ageStats, totalStats, isEmpty)),
            osLens.modify(mapDimensionFunnel(osStats, totalStats, isEmpty)),
            countryLens.modify(mapDimensionFunnel(countryStats, totalStats, isEmpty)),
            genderLens.modify(mapDimensionFunnel(ageGenderStats, totalStats, isEmpty, "GENDER")),
            totalLens.modify(mapDimensionFunnel(totalStats, totalStats, isEmpty, "TOTAL")),
        )(createDefaultDimensionsFunnelMap()),
        isEmpty,
    };
};
